// ===========================================
// MODIFIER GROUPS MANAGER
// ===========================================
// Create and edit modifier groups (Temperature, Sides, Add-ons…) and their
// options. Groups attach to items in the item form; POS then shows exactly
// the right modifiers per item instead of everything.

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, X, SlidersHorizontal } from 'lucide-react';
import { menuApi, inventoryApi } from '../services/api';

export interface ModifierGroup {
  id: string;
  name: string;
  required: boolean;
  minSelections: number;
  maxSelections: number;
  modifiers: Array<{ id?: string; name: string; priceAdjustmentCents: number; ingredientId?: string | null; ingredientQuantity?: number | string | null }>;
  linkedItemIds: string[];
}

export function useModifierGroups() {
  return useQuery({
    queryKey: ['modifier-groups'],
    queryFn: async () => {
      const response = await menuApi.getModifierGroups();
      return (response.data?.data || []) as ModifierGroup[];
    },
  });
}

interface EditorState {
  id?: string;
  name: string;
  required: boolean;
  maxSelections: number;
  modifiers: Array<{ name: string; priceAdjustmentCents: number; ingredientId?: string; ingredientQuantity?: number }>;
}

const emptyEditor: EditorState = {
  name: '',
  required: false,
  maxSelections: 1,
  modifiers: [{ name: '', priceAdjustmentCents: 0 }],
};

export default function ModifierGroupsManager() {
  const queryClient = useQueryClient();
  const { data: groups } = useModifierGroups();
  // Inventory list for the optional "uses ingredient" link (stock deduction)
  const { data: invData } = useQuery({
    queryKey: ['inventory'],
    queryFn: async () => {
      const response = await inventoryApi.getItems();
      const result = response.data?.data || response.data;
      return Array.isArray(result) ? result : [];
    },
    staleTime: 60 * 1000,
  });
  const inventoryItems: Array<{ id: string; name: string; unit: string }> = invData || [];
  const [expanded, setExpanded] = useState(false);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [error, setError] = useState('');

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['modifier-groups'] });
    queryClient.invalidateQueries({ queryKey: ['menu-items'] });
  };

  const saveMutation = useMutation({
    mutationFn: async (state: EditorState) => {
      const payload = {
        name: state.name.trim(),
        required: state.required,
        minSelections: state.required ? 1 : 0,
        maxSelections: state.maxSelections,
        modifiers: state.modifiers
          .filter((m) => m.name.trim())
          .map((m) => ({
            name: m.name.trim(),
            priceAdjustmentCents: m.priceAdjustmentCents || 0,
            ...(m.ingredientId ? { ingredientId: m.ingredientId, ingredientQuantity: m.ingredientQuantity || 0 } : {}),
          })),
      };
      if (state.id) {
        return menuApi.updateModifierGroup(state.id, payload);
      }
      return menuApi.createModifierGroup(payload);
    },
    onSuccess: () => {
      refresh();
      setEditor(null);
      setError('');
    },
    onError: (err: any) => {
      setError(err.response?.data?.error?.message || "Couldn't save the group.");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (groupId: string) => menuApi.deleteModifierGroup(groupId),
    onSuccess: refresh,
  });

  const formatPrice = (cents: number) =>
    cents === 0 ? '' : ` (+$${(cents / 100).toFixed(2)})`;

  return (
    <div className="card p-4 mb-6">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between"
      >
        <div className="flex items-center gap-2">
          <SlidersHorizontal className="w-5 h-5 text-primary-600" />
          <h2 className="font-semibold text-slate-900">Modifiers</h2>
          <span className="text-sm text-slate-400">
            {groups?.length ? `${groups.length} group${groups.length === 1 ? '' : 's'}` : 'none yet'}
          </span>
        </div>
        <span className="text-sm text-primary-600 font-medium">{expanded ? 'Hide' : 'Manage'}</span>
      </button>

      {expanded && (
        <div className="mt-4 space-y-2">
          {(groups || []).map((g) => (
            <div key={g.id} className="flex items-center justify-between px-3 py-2.5 bg-slate-50 rounded-lg">
              <div className="min-w-0">
                <p className="font-medium text-slate-800">
                  {g.name}
                  {g.required && <span className="ml-2 text-xs text-amber-600 font-semibold">required</span>}
                </p>
                <p className="text-xs text-slate-500 truncate">
                  {g.modifiers.map((m) => `${m.name}${formatPrice(m.priceAdjustmentCents)}`).join(', ') || 'no options yet'}
                  {' · '}on {g.linkedItemIds.length} item{g.linkedItemIds.length === 1 ? '' : 's'}
                </p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={() =>
                    setEditor({
                      id: g.id,
                      name: g.name,
                      required: g.required,
                      maxSelections: g.maxSelections,
                      modifiers: g.modifiers.map((m) => ({
                        name: m.name,
                        priceAdjustmentCents: m.priceAdjustmentCents,
                        ingredientId: m.ingredientId || undefined,
                        ingredientQuantity: m.ingredientQuantity != null ? parseFloat(String(m.ingredientQuantity)) : undefined,
                      })),
                    })
                  }
                  className="p-2 text-slate-500 hover:bg-slate-200 rounded-lg"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  onClick={() => {
                    if (window.confirm(`Delete "${g.name}"? It comes off every item that uses it.`)) {
                      deleteMutation.mutate(g.id);
                    }
                  }}
                  className="p-2 text-red-400 hover:bg-red-50 rounded-lg"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}

          <button
            onClick={() => setEditor({ ...emptyEditor, modifiers: [{ name: '', priceAdjustmentCents: 0 }] })}
            className="flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 mt-2"
          >
            <Plus className="w-4 h-4" /> New modifier group
          </button>
        </div>
      )}

      {/* Editor modal */}
      {editor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl max-h-[90dvh] overflow-y-auto">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-bold text-slate-900">
                {editor.id ? 'Edit modifier group' : 'New modifier group'}
              </h3>
              <button onClick={() => setEditor(null)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Group name</label>
                <input
                  type="text"
                  value={editor.name}
                  onChange={(e) => setEditor({ ...editor, name: e.target.value })}
                  placeholder="e.g. Temperature, Sides, Add-ons"
                  className="input w-full"
                />
              </div>

              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={editor.required}
                    onChange={(e) => setEditor({ ...editor, required: e.target.checked })}
                    className="rounded text-primary-600"
                  />
                  Guest must choose one
                </label>
                <label className="flex items-center gap-2 text-sm">
                  Max choices
                  <input
                    type="number"
                    min={1}
                    value={editor.maxSelections}
                    onChange={(e) =>
                      setEditor({ ...editor, maxSelections: Math.max(1, parseInt(e.target.value) || 1) })
                    }
                    className="input w-16 py-1"
                  />
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Options</label>
                <div className="space-y-2">
                  {editor.modifiers.map((m, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={m.name}
                        onChange={(e) => {
                          const mods = [...editor.modifiers];
                          mods[i] = { ...mods[i], name: e.target.value };
                          setEditor({ ...editor, modifiers: mods });
                        }}
                        placeholder="Option name"
                        className="input flex-1"
                      />
                      <div className="relative w-28">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">+$</span>
                        <input
                          type="number"
                          step="0.25"
                          min="0"
                          value={m.priceAdjustmentCents ? (m.priceAdjustmentCents / 100).toString() : ''}
                          onChange={(e) => {
                            const mods = [...editor.modifiers];
                            mods[i] = {
                              ...mods[i],
                              priceAdjustmentCents: Math.round(parseFloat(e.target.value || '0') * 100),
                            };
                            setEditor({ ...editor, modifiers: mods });
                          }}
                          placeholder="0.00"
                          className="input w-full pl-8"
                        />
                      </div>
                      <button
                        onClick={() => {
                          const mods = editor.modifiers.filter((_, idx) => idx !== i);
                          setEditor({ ...editor, modifiers: mods.length ? mods : [{ name: '', priceAdjustmentCents: 0 }] });
                        }}
                        className="p-2 text-red-400 hover:bg-red-50 rounded-lg"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>

                {/* Optional stock link per option — selling the option deducts
                    this amount of the chosen inventory item */}
                <div className="mt-2 space-y-1.5">
                  {editor.modifiers.map((m, i) => (
                    <div key={`ing-${i}`} className="flex items-center gap-2 pl-1">
                      <span className="text-xs text-slate-400 w-24 truncate">{m.name || `Option ${i + 1}`}</span>
                      <select
                        value={m.ingredientId || ''}
                        onChange={(e) => {
                          const mods = [...editor.modifiers];
                          mods[i] = { ...mods[i], ingredientId: e.target.value || undefined };
                          setEditor({ ...editor, modifiers: mods });
                        }}
                        className="input flex-1 text-sm py-1.5"
                      >
                        <option value="">No stock deduction</option>
                        {inventoryItems.map((it) => (
                          <option key={it.id} value={it.id}>uses {it.name}</option>
                        ))}
                      </select>
                      {m.ingredientId && (
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={m.ingredientQuantity ?? ''}
                          onChange={(e) => {
                            const mods = [...editor.modifiers];
                            mods[i] = { ...mods[i], ingredientQuantity: parseFloat(e.target.value) || 0 };
                            setEditor({ ...editor, modifiers: mods });
                          }}
                          placeholder="qty"
                          title={`Amount deducted per sale, in the item's stock unit (${inventoryItems.find((it) => it.id === m.ingredientId)?.unit || 'unit'})`}
                          className="input w-24 text-sm py-1.5"
                        />
                      )}
                    </div>
                  ))}
                </div>
                <button
                  onClick={() =>
                    setEditor({ ...editor, modifiers: [...editor.modifiers, { name: '', priceAdjustmentCents: 0 }] })
                  }
                  className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium mt-2"
                >
                  <Plus className="w-4 h-4" /> Add option
                </button>
              </div>

              {error && <p className="text-sm text-red-600">{error}</p>}

              <button
                onClick={() => saveMutation.mutate(editor)}
                disabled={!editor.name.trim() || saveMutation.isPending}
                className="w-full py-3 rounded-xl bg-primary-600 text-white font-semibold disabled:opacity-50"
              >
                {saveMutation.isPending ? 'Saving…' : 'Save group'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
